import gql from "graphql-tag";
import GraphQLClient, { RequestParam } from "../api";
import {
  BillingInfo,
  RegisterCustomerInput,
  CreateCheckoutSessionInput,
  CreatePortalSessionInput,
  CreateOneTimeCheckoutSessionInput,
  CheckoutSession,
  PortalSession,
  PlanPricing,
} from "../models/billing";

interface IBillingClient {
  fetchBillingInfo: () => Promise<BillingInfo>;
  registerCustomer: (payload: RegisterCustomerInput) => Promise<boolean>;
  createCheckoutSession: (payload: CreateCheckoutSessionInput) => Promise<CheckoutSession>;
  createOneTimeCheckoutSession: (payload: CreateOneTimeCheckoutSessionInput) => Promise<CheckoutSession>;
  getPlanPricing: () => Promise<PlanPricing>;
}

export class BillingClient implements IBillingClient {
  async fetchBillingInfo(): Promise<BillingInfo> {
    const { billingInfo } = await GraphQLClient.request({ query: fetchBillingInfo });
    return billingInfo;
  }

  async registerCustomer(payload: RegisterCustomerInput): Promise<boolean> {
    const { token, verificationToken, email } = payload;

    const params: RequestParam = {
      query: registerCustomerQuery,
      variables: {
        input: {
          token,
          verificationToken,
          email,
        },
      },
    };

    const { registerCustomer } = await GraphQLClient.request(params);
    return registerCustomer.result;
  }

  async createCheckoutSession(payload: CreateCheckoutSessionInput): Promise<CheckoutSession> {
    const { email, plan, successUrl, cancelUrl } = payload;
    const params: RequestParam = {
      query: createCheckoutSessionQuery,
      variables: {
        input: {
          email,
          plan,
          successUrl,
          cancelUrl,
        },
      },
    };

    const { createCheckoutSession } = await GraphQLClient.request(params);
    return createCheckoutSession;
  }

  async createPortalSession(payload: CreatePortalSessionInput): Promise<PortalSession> {
    const { returnUrl } = payload;
    const params: RequestParam = {
      query: createPortalSessionQuery,
      variables: {
        input: {
          returnUrl,
        },
      },
    };

    const { createPortalSession } = await GraphQLClient.request(params);
    return createPortalSession;
  }

  async createOneTimeCheckoutSession(payload: CreateOneTimeCheckoutSessionInput): Promise<CheckoutSession> {
    const { email, itemId, successUrl, cancelUrl } = payload;
    const params: RequestParam = {
      query: createOneTimeCheckoutSessionQuery,
      variables: {
        input: {
          email,
          itemId,
          successUrl,
          cancelUrl,
        },
      },
    };

    const { createOneTimeCheckoutSession } = await GraphQLClient.request(params);
    return createOneTimeCheckoutSession;
  }

  async getPlanPricing(): Promise<PlanPricing> {
    const { getPlanPricing } = await GraphQLClient.request({ query: getPlanPricingQuery });
    return getPlanPricing;
  }
}

/**
 * Query
 */

const fetchBillingInfo = gql`
  {
    billingInfo {
      plan
      stripeId
      platform
      invoiceUrl
      cardInfo {
        exp_month
        exp_year
        last4
      }
      subscription {
        created
        currentPeriodStart
        currentPeriodEnd
        # 後方互換のため残っているが、状態判定には renewalState を使う
        isAutoRenewal
        renewalState
      }
    }
  }
`;

const registerCustomerQuery = gql`
  mutation RegisterCustomer($input: RegisterCustomerInput!) {
    registerCustomer(input: $input) {
      result
    }
  }
`;

const createCheckoutSessionQuery = gql`
  mutation CreateCheckoutSession($input: CreateCheckoutSessionInput!) {
    createCheckoutSession(input: $input) {
      id
      url
    }
  }
`;

const createPortalSessionQuery = gql`
  mutation CreatePortalSession($input: CreatePortalSessionInput!) {
    createPortalSession(input: $input) {
      id
      url
    }
  }
`;

const createOneTimeCheckoutSessionQuery = gql`
  mutation CreateOneTimeCheckoutSession($input: CreateOneTimeCheckoutSessionInput!) {
    createOneTimeCheckoutSession(input: $input) {
      id
      url
    }
  }
`;

const getPlanPricingQuery = gql`
  {
    getPlanPricing {
      monthlyPrice
      yearlyPrice
    }
  }
`;
